<template>
    <div class="opportunity-statuses-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Opportunity Stages</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="add-opportunity-statuses-buttons-wrapper">
                    <app-button v-if="!isAddOpportunityStatusShown" @click="showAddOpportunityStatusSection">
                        Add New
                    </app-button>
                </div>
                <div class="add-opportunity-status-wrapper" v-if="isAddOpportunityStatusShown">
                    <div class="flex-wrapper">
                        <h3 class="upper-text">Add New Opportunity Stage</h3>
                        <div class="add-opportunity-status-color-wrapper">
                            <div class="app-multiselect-new-wrapper">
                                <multiselect class="app-multiselect-new"
                                    v-model="opportunityStatus.color"
                                    :options="colors"
                                    :show-labels="false"
                                    :searchable="false"
                                    placeholder="Color">
                                    <template slot="singleLabel" slot-scope="props">
                                        <div class="item" :style="{  background: props.option }"></div>
                                    </template>
                                    <template slot="option" slot-scope="props">
                                        <div class="item" :style="{  background: props.option }"></div>
                                    </template>
                                    <template slot="noResult">No results</template>
                                    <template slot="noOptions">No colors</template>
                                </multiselect>
                            </div>
                        </div>
                        <app-input
                            v-model="opportunityStatus.name" 
                            :label="'Name'" />
                    </div>
                    <app-input-radio v-model="opportunityStatus.isWon" :options="statusOptions" />
                    <div class="actions">
                        <app-button 
                            @click="saveNewOpportunityStatus" 
                            :loading="isSavingNewOpportunityStatus" 
                            :disabled="isSavingNewOpportunityStatus"
                            size="small">
                            Save
                        </app-button>
                        <app-button 
                            class="red-btn"
                            @click="hideAddOpportunityStatusSection"
                            size="small">
                            Cancel
                        </app-button>
                    </div>
                </div>
                <div class="opportunity-statuses-wrapper">
                    <div 
                        v-for="opportunityStatus in opportunityStatuses" 
                        :class="['opportunity-status', { 'editing': opportunityStatus.isEditShown }]" 
                        :key="opportunityStatus.id">
                        <div class="opportunity-status-wrapper">
                            <div class="opportunity-status-info">
                                <div class="edit-opportunity-status-color-wrapper" v-if="opportunityStatus.isEditShown">
                                    <div class="flex-wrapper">
                                        <div class="app-multiselect-new-wrapper">
                                            <multiselect class="app-multiselect-new"
                                                v-model="opportunityStatus.color.edit"
                                                :options="colors"
                                                :show-labels="false"
                                                :searchable="false"
                                                placeholder="Color">
                                                <template slot="singleLabel" slot-scope="props">
                                                    <div class="item" :style="{ background: props.option }"></div>
                                                </template>
                                                <template slot="option" slot-scope="props">
                                                    <div class="item" :style="{ background: props.option }"></div>
                                                </template>
                                                <template slot="noResult">No results</template>
                                                <template slot="noOptions">No colors</template>
                                            </multiselect>
                                        </div>

                                        <app-input
                                            v-model="opportunityStatus.name.edit" 
                                            :label="'Name'"  />
                                    </div>
                                    <app-input-radio v-model="opportunityStatus.isWon.edit" :options="statusOptions" />
                                </div>
                                <div v-if="!opportunityStatus.isEditShown" class="color" :style="{ background: opportunityStatus.color.edit }"></div>
                                <p v-if="!opportunityStatus.isEditShown">{{ opportunityStatus.name.value }}</p>
                                
                            </div>
                        </div>
                        <div v-if="opportunityStatus.isEditShown" class="opportunity-status-edit-buttons-wrapper actions">
                            <app-button
                                @click="updateOpportunityStatus(opportunityStatus)"
                                size="small"
                                :disabled="opportunityStatus.isUpdating"
                                :loading="opportunityStatus.isUpdating">
                                Save
                            </app-button>
                            <app-button 
                                @click="cancelOpportunityStatusEdit(opportunityStatus)"
                                size="small"
                                class="red-btn"
                                :disabled="opportunityStatus.isUpdating"
                                :loading="opportunityStatus.isUpdating">
                                Cancel
                            </app-button>
                        </div>
                        <div v-else class="opportunity-status-edit-buttons-wrapper">
                            <app-button
                                @click="showOpportunityStatusEdit(opportunityStatus)"
                                size="small"
                                :disabled="opportunityStatus.isDeleting">
                                Edit
                            </app-button>
                            <app-button
                                @click="deleteOpportunityStatus(opportunityStatus)"
                                class="red-btn"
                                size="small"
                                :disabled="opportunityStatus.isDeleting"
                                :loading="opportunityStatus.isDeleting">
                                Delete
                            </app-button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container"></div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const AppInputRadio = () => import('@/components/common/app-input-radio');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const Multiselect = () => import('vue-multiselect');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'opportunity-statuses-widget',

    props: {
        options: {
            type: Object
        },

        widget: {
            type: Object,
            required: true
        }
    },

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'app-input-radio': AppInputRadio,
        'widget-close-svg': WidgetCloseSvg,
        'multiselect': Multiselect
    },

    data() {
        return {
            opportunityStatuses: [],
            isAddOpportunityStatusShown: false,
            isColorPickerShown: false,
            isSavingNewOpportunityStatus: false,
            opportunityStatus: {
                name: '',
                color: 'Grey',
                isWon: null
            },

            statusOptions: [{ 
                name: 'Won',
                value: 'Won'
            }, {
                name: 'In Progress',
                value: 'InProgress'
            }, {
                name: 'Lost',
                value: 'Lost'
            }],

            colors: [
                'Purple',
                'DarkBlue',
                'Orange',
                'Red',
                'Pink',
                'Blue',
                'Green',
                'Grey',
            ],

            clientId: null
        }
    },

    methods: {
        getOpportunityStatuses() {
            this.$api
                .get(`/service/DealStatuses/?$filter=ClientId eq ${ this.clientId }`)
                .then(response => {
                    var opportunityStatusResponse = response.data.value;
                    this.setOpportunityStatuses(opportunityStatusResponse);
                })
                .catch(error => {
                    this.$helpers.showFailNotification('An error occurred while getting your opportunity stages', { error });
                });
        },

        setOpportunityStatuses(response) {
            this.opportunityStatuses = response.map(opportunityStatus => {
                return {
                    id: opportunityStatus.Id,
                    name: {
                        value: opportunityStatus.Name,
                        edit: opportunityStatus.Name
                    },
                    color: {
                        value: opportunityStatus.Color,
                        edit: opportunityStatus.Color    
                    },
                    isWon: {
                        value: this.getStatusValueByBoolean(opportunityStatus.IsWon),
                        edit: this.getStatusValueByBoolean(opportunityStatus.IsWon)    
                    },
                    isEditShown: false,
                    isUpdating: false,
                    isDeleting: false
                };
            });
        },

        getStatusValueByBoolean(isWon) {
            switch(isWon) {
                case true:
                    return this.statusOptions[0];
                    break;
                case false:
                    return this.statusOptions[2];
                    break;
                case null:
                    return this.statusOptions[1];
                    break;
            }
        },

        getBooleanByStatusValue(statusValue) {
            switch(statusValue) {
                case 'Won':
                    return true;
                    break;
                case 'Lost':
                    return false;
                    break;
                case 'InProgress':
                    return null;
                    break;
            }
        },

        showAddOpportunityStatusSection() {
            this.isAddOpportunityStatusShown = true;
        },

        hideAddOpportunityStatusSection() {
            this.isAddOpportunityStatusShown = false;
        },

        saveNewOpportunityStatus() {
            this.isSavingNewOpportunityStatus = true;

            var request = {
                Name: this.opportunityStatus.name,
                Color: this.opportunityStatus.color,
                ClientId: this.clientId,
                IsWon: this.getBooleanByStatusValue(this.opportunityStatus.isWon.value)
            };

            this.$api
                .post('/api/DealStatuses/', request)
                .then(response => {
                    this.opportunityStatuses.unshift({
                        id: response.data.Id,
                        name: {
                            value: response.data.Name,
                            edit: response.data.Name
                        },
                        color: {
                            value: response.data.Color,
                            edit: response.data.Color    
                        },
                        isWon: {
                            value: this.opportunityStatus.isWon.value,
                            edit: this.opportunityStatus.isWon.value
                        },
                        isEditShown: false,
                        isUpdating: false,
                        isDeleting: false
                    });

                    this.isAddOpportunityStatusShown = false;

                    this.clearOpportunityStatus();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while creating your opportunity stage.', { error })
                })
                .finally(() => {
                    this.isSavingNewOpportunityStatus = false;
                });
        },

        clearOpportunityStatus() {
            this.opportunityStatus.name = '';
            this.opportunityStatus.color = '#FFFFFF';
            this.opportunityStatus.isWon = this.statusOptions[0];
        },

        setOpportunityStatusColor(color) {
            this.opportunityStatus.color = color.hex;
        },

        updateOpportunityStatus(opportunityStatus) {
            opportunityStatus.isUpdating = true;

            var changes = [{
                'name': 'Name',
                'value': opportunityStatus.name.edit
            }, {
                'name': 'Color',
                'value': opportunityStatus.color.edit
            }, {
                'name': 'IsWon',
                'value': this.getBooleanByStatusValue(opportunityStatus.isWon.edit.value)
            }];

            this.$api
                .patch(`/api/DealStatuses/${ opportunityStatus.id }`, changes)
                .then(response => {
                    opportunityStatus.isUpdating = false;
                    opportunityStatus.isEditShown = false;

                    opportunityStatus.color.value = opportunityStatus.color.edit;
                    opportunityStatus.name.value = opportunityStatus.name.edit;

                    opportunityStatus.isEditShown = false;
                })
                .catch(error => {
                    opportunityStatus.isUpdating = false;
                    opportunityStatus.isEditShown = false;
                    this.$helpers.showFailNotification('An error occurred while updating your opportunity stage.', { error })
                });
        },

        showOpportunityStatusEdit(opportunityStatus) {
            opportunityStatus.isEditShown = true;

        },

        cancelOpportunityStatusEdit(opportunityStatus) {
            opportunityStatus.color.edit = opportunityStatus.color.value;
            opportunityStatus.name.edit = opportunityStatus.name.value;
            opportunityStatus.isWon.edit = opportunityStatus.isWon;
            opportunityStatus.isEditShown = false;
        },

        updateOpportunityStatusColor(color, opportunityStatus) {
            opportunityStatus.color.edit = color;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        deleteOpportunityStatus(opportunityStatus) {
            opportunityStatus.isDeleting = true;

            this.$api
                .delete(`/api/DealStatuses/${ opportunityStatus.id }`)
                .then(response => {
                    opportunityStatus.isDeleting = false;
                    var matchedOpportunityStatus = this.opportunityStatuses.find(type => {
                        return type.id === opportunityStatus.id;
                    });

                    this.opportunityStatuses.splice(this.opportunityStatuses.indexOf(matchedOpportunityStatus), 1);
                    this.$helpers.showSuccessNotification(`The "${ opportunityStatus.name.edit }" opportunity stage has been removed.`);
                })
                .catch(error => {
                    this.isDeleting = false;
                    opportunityStatus.isEditShown = false;
                    this.$helpers.showFailNotification(`An error occurred while deleting "${ opportunityStatus.name.edit }".`, { error });
                });
        },
    },

    created() {
        this.clientId = this.widget.options.clientId;
        if(!this.opportunityStatus.isWon){
            this.opportunityStatus.isWon = this.statusOptions[0];
        }
        
        this.getOpportunityStatuses();
    }
}
</script>

<style>
.opportunity-statuses-widget .flex-wrapper {
    display: flex;
    align-items: flex-end;
}

.opportunity-statuses-widget .content-wrapper .add-opportunity-statuses-buttons-wrapper .btn {
    margin-bottom: 25px;
}

.opportunity-statuses-widget .content-wrapper .add-opportunity-status-wrapper {
    margin-bottom: 25px;
    padding: 25px 15px 15px 15px;
    border: 2px solid #E9E9EF;
    border-radius: 20px;
    position: relative;
}

.opportunity-statuses-widget .content-wrapper .add-opportunity-status-wrapper .upper-text {
    position: absolute;
    top: -10px;
    left: 15px;
    background: #FFFFFF;
    padding: 0px 15px;
}

.opportunity-statuses-widget .content-wrapper .add-opportunity-status-wrapper .add-opportunity-status-color-wrapper,
.opportunity-statuses-widget .content-wrapper .opportunity-status .opportunity-status-wrapper .opportunity-status-info .color {
    position: relative;
    margin-right: 15px;
}

.opportunity-statuses-widget .content-wrapper .add-opportunity-status-wrapper .add-opportunity-status-color-wrapper .item,
.opportunity-statuses-widget .content-wrapper .opportunity-status .opportunity-status-wrapper .opportunity-status-info .color,
.opportunity-statuses-widget .content-wrapper .opportunity-status-wrapper .edit-opportunity-status-color-wrapper .item {
    height: 30px;
    width: 30px;
    border-radius: 50%;
    border: 2px solid #E9E9EF;
}

.opportunity-statuses-widget .content-wrapper .add-opportunity-status-wrapper .app-input {
    width: 100%;
}

.opportunity-statuses-widget .content-wrapper .options {
    display: flex;
    justify-content: center;
    margin: 15px -10px;
}

.opportunity-statuses-widget .content-wrapper .options .option {
    margin: 0 10px;
}

.opportunity-statuses-widget .content-wrapper .actions {
    display: flex;
    justify-content: center;
    margin-top: 10px;
    width: 100%;
}

.opportunity-statuses-widget .content-wrapper .actions .btn {
    margin: 0 5px;
}

.opportunity-statuses-widget .content-wrapper .opportunity-statuses-wrapper .opportunity-status {
    padding: 5px 10px;
    border-radius: 20px;
    border: 2px solid #E9E9EF;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
}

.opportunity-statuses-widget .content-wrapper .opportunity-statuses-wrapper .opportunity-status .opportunity-status-wrapper,
.opportunity-statuses-widget .content-wrapper .opportunity-statuses-wrapper .opportunity-status .opportunity-status-wrapper .opportunity-status-info {
    display: flex;
    align-items: center;
}

.opportunity-statuses-widget .content-wrapper .opportunity-statuses-wrapper .opportunity-status .opportunity-status-wrapper .opportunity-status-color {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    margin-right: 10px;
    border: 1px solid #E9E9EF;
}

.opportunity-statuses-widget .content-wrapper .opportunity-statuses-wrapper .opportunity-status .opportunity-status-wrapper p {
    color: #877D88;
    font-size: 16px;
    margin-bottom: 0;
}

.opportunity-statuses-widget .content-wrapper .opportunity-statuses-wrapper .opportunity-status .opportunity-status-edit-buttons-wrapper {
    display: flex;
}

.opportunity-statuses-widget .content-wrapper .opportunity-statuses-wrapper .opportunity-status .opportunity-status-edit-buttons-wrapper .btn:nth-child(1) {
    margin-right: 10px;
}

.opportunity-statuses-widget .content-wrapper .opportunity-statuses-wrapper .opportunity-status.editing .opportunity-status-wrapper {
    display: block;
    width: 100%;
}

.opportunity-statuses-widget .content-wrapper .opportunity-statuses-wrapper .opportunity-status.editing .app-input {
    width: 100%;
}

.opportunity-statuses-widget .content-wrapper .opportunity-statuses-wrapper .opportunity-status.editing .edit-opportunity-status-color-wrapper {
    width: 100%;
}

.opportunity-statuses-widget .content-wrapper .opportunity-statuses-wrapper .opportunity-status .opportunity-status-edit-buttons-wrapper .red-btn {
    background: #F30070;
    border: 1px solid #F30070;
    color: #FFFFFF;
}

.opportunity-statuses-widget .content-wrapper .opportunity-statuses-wrapper .opportunity-status .opportunity-status-edit-buttons-wrapper .red-btn:hover {
    background: #D10E68;
    border: 1px solid #D10E68;
}

.opportunity-statuses-widget .red-btn {
    background: #F30070;
    border: 1px solid #F30070;
    color: #FFFFFF;
}

.opportunity-statuses-widget .red-btn:hover {
    background: #D10E68;
    border: 1px solid #D10E68;
}

.opportunity-statuses-widget .flex-wrapper {
    width: 100%;
}

.opportunity-statuses-widget .app-multiselect-new-wrapper {
    width: 85px;
    min-width: 85px;
    max-height: 40px;
}

.opportunity-statuses-widget .app-multiselect-new.multiselect .multiselect__tags .multiselect__placeholder {
    font-size: 14px;
    padding: 6px;
}

.opportunity-statuses-widget .app-multiselect-new.multiselect .multiselect__tags,
.opportunity-statuses-widget .app-multiselect-new.multiselect input {
    padding: 3px 0 0 9px;
}
</style>